import * as THREE from 'three';
import { STREAK_TIERS, STREAK_RULES } from '../modes/rules.js';

/**
 * Killstreaks: UAV, precision airstrike, attack helo.
 *
 * The HUD has advertised these three since the rail was drawn, with nothing
 * behind them. Everything here is built on machinery that already exists —
 * `Game.detonate` does line-of-sight splash, `Effects.explosion` does the puff,
 * the minimap already receives every enemy position — so the new code is
 * scheduling and presentation rather than new systems.
 *
 * One key (F) spends the highest tier you have earned. Weapons own 1-7, G and Q
 * are throwables, E is use, R reload, Tab the board; F was free and is where a
 * CoD player's hand already goes for equipment.
 */

const _v = new THREE.Vector3();
const _d = new THREE.Vector3();

// The tiers and their numbers live in src/modes/rules.js, because online the
// server has to agree with this file about what has been earned and what it is
// worth. Re-exported so existing importers do not care where they moved to.
export { STREAK_TIERS };

const UAV_TIME = STREAK_RULES.uavTime;
const STRIKE_DELAY = STREAK_RULES.strikeDelay;
const STRIKE_BOMBS = STREAK_RULES.strikeBombs;
const STRIKE_SPACING = STREAK_RULES.strikeSpacing;
const STRIKE_RADIUS = STREAK_RULES.strikeRadius;
const STRIKE_DAMAGE = STREAK_RULES.strikeDamage;
const HELI_TIME = STREAK_RULES.heliTime;
const HELI_ALT = STREAK_RULES.heliAlt;
const HELI_RADIUS = STREAK_RULES.heliRadius;
const HELI_DPS = STREAK_RULES.heliDps;

export class Killstreaks {
  constructor(game) {
    this.game = game;
    /** Tiers earned and not yet spent, highest last. */
    this.earned = [];
    this.uav = 0;
    this.strikes = [];
    this.heli = null;
    this._lastKills = 0;
  }

  /** Highest earned tier, or null. */
  get ready() {
    return this.earned.length ? this.earned[this.earned.length - 1] : null;
  }

  get uavActive() { return this.uav > 0; }

  /**
   * Award tiers as the kill count passes them. Driven from the HUD's counter
   * because that is where kills already live.
   */
  syncKills(kills) {
    // Online the ledger is the server's. Awarding here as well would let a
    // client hand itself a tier the server will then refuse to honour, which
    // reads as a killstreak that silently does nothing — the exact bug this
    // whole change exists to remove.
    if (this.game.net) return;
    if (kills <= this._lastKills) { this._lastKills = kills; return; }
    for (const t of STREAK_TIERS) {
      if (kills >= t.at && this._lastKills < t.at && !this.earned.some((e) => e.id === t.id)) {
        this.earned.push(t);
        this.game.hud.streakToast = { name: t.name, sub: 'READY — PRESS F', t: 0 };
        this.game.audio?.streakReady?.();
      }
    }
    this._lastKills = kills;
  }

  /** Spend the highest earned tier. Returns the tier used, or null. */
  use() {
    const tier = this.ready;
    if (!tier || !this.game.player.alive) return null;

    // Online this is a REQUEST. The server holds the ledger — it is the only
    // thing that can tell an earned airstrike from a fabricated one — so the
    // tier is not spent here and the effect does not start here. Both come back
    // as events. What this client still owns is the AIM: where the strike is
    // marked, exactly as it owns where a rocket landed.
    if (this.game.net) {
      this.game.net.callStreak(tier.id, tier.id === 'strike' ? this._markStrike() : null);
      return tier;
    }

    this.earned.pop();
    if (tier.id === 'uav') this._callUav();
    else if (tier.id === 'strike') this._callStrike();
    else this._callHeli();
    return tier;
  }

  /** Where the player is looking, which is where the run will walk through. */
  _markStrike() {
    const g = this.game;
    const cam = g.engine.camera;
    cam.getWorldDirection(_d);
    const hit = g.physics.raycast(cam.position, _d, STREAK_RULES.strikeMark);
    return hit ? hit.point.clone() : cam.position.clone().addScaledVector(_d, 90);
  }

  /**
   * The server has granted something. Held tiers are replaced wholesale rather
   * than merged: it is the authority on what you hold, including after a
   * refusal, and a client-side merge would quietly resurrect a spent one.
   */
  setHeld(ids) {
    this.earned = STREAK_TIERS.filter((t) => ids.includes(t.id));
  }

  _callUav() {
    this.uav = UAV_TIME;
    this.game.hud.showObjective?.('UAV ONLINE', 'ENEMY POSITIONS REVEALED', 2.4);
    this.game.audio?.uav?.();
  }

  /**
   * A run of bombs walking through wherever the player is looking. Aiming it
   * with the crosshair keeps the whole thing to one keypress — no map screen,
   * no second input mode.
   */
  _callStrike() {
    const g = this.game;
    const cam = g.engine.camera;
    cam.getWorldDirection(_d);
    const hit = g.physics.raycast(cam.position, _d, 260);
    const centre = hit ? hit.point.clone() : cam.position.clone().addScaledVector(_d, 90);

    // Walk the run along the player's facing so it reads as a deliberate line.
    const along = _d.clone().setY(0).normalize();
    if (along.lengthSq() < 1e-4) along.set(0, 0, -1);

    for (let i = 0; i < STRIKE_BOMBS; i++) {
      const t = i - (STRIKE_BOMBS - 1) / 2;
      const at = centre.clone().addScaledVector(along, t * STRIKE_SPACING);
      // Scatter so it does not look like a ruler.
      at.x += (Math.random() - 0.5) * 3.4;
      at.z += (Math.random() - 0.5) * 3.4;
      this.strikes.push({ at, t: STRIKE_DELAY + i * 0.14 });
    }
    g.hud.showObjective?.('AIRSTRIKE INBOUND', 'CLEAR THE AREA', 2.6);
    g.audio?.strikeInbound?.();
  }

  /**
   * @param at  centre and starting angle, when the SERVER is flying it. Both
   *   sides then compute the orbit from the same numbers and stay in step
   *   without anyone streaming a position 20 times a second.
   */
  _callHeli(at = null) {
    const g = this.game;
    const centre = at ? new THREE.Vector3(at.c[0], 0, at.c[1]) : g.player.position.clone();
    centre.y = 0;
    this.heli = {
      t: at?.d ?? HELI_TIME,
      angle: at ? at.ang : Math.random() * Math.PI * 2,
      centre,
      pos: new THREE.Vector3(),
      target: null,
      retarget: 0,
      fire: 0,
      mesh: this._heliMesh(),
    };
    g.engine.scene.add(this.heli.mesh);
    // The banner is the caller's only when this machine called it. Online it
    // depends on which side you are on, so NetSession says it instead.
    if (!at) g.hud.showObjective?.('ATTACK HELO', 'GUNSHIP ON STATION', 2.6);
    g.audio?.heliIn?.();
  }

  /** Take the gunship off station because the server says it has gone. */
  removeHeli() {
    const h = this.heli;
    if (!h) return;
    this.game.engine.scene.remove(h.mesh);
    h.mesh.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
    this.heli = null;
  }

  /**
   * Procedural gunship, AH-64 shaped.
   *
   * The first pass was a capsule under a big translucent disc, and close up it
   * read as a black blob wearing a saucer. What makes a helicopter legible is
   * the silhouette: tapered fuselage, tandem canopy, stub wings with pylons,
   * a long boom with a tail rotor, and BLADES rather than a disc — a disc only
   * works as motion blur, and there is none here.
   */
  _heliMesh() {
    const g = new THREE.Group();
    // Sun plus sky IBL lifts a mid grey-green into sand; keep the albedo low
    // or the gunship reads as desert tan against the sky rather than a
    // silhouette.
    const body = new THREE.MeshStandardMaterial({ color: 0x22261f, roughness: 0.78, metalness: 0.18 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x16191b, roughness: 0.6, metalness: 0.35 });
    const glass = new THREE.MeshStandardMaterial({
      color: 0x0d1216, roughness: 0.16, metalness: 0.6,
    });

    const add = (geo, mat, x, y, z, rx = 0, ry = 0, rz = 0, sx = 1, sy = 1, sz = 1) => {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(x, y, z);
      m.rotation.set(rx, ry, rz);
      m.scale.set(sx, sy, sz);
      g.add(m);
      return m;
    };

    // Fuselage: a long box tapering to the nose, with the belly rounded off.
    add(new THREE.BoxGeometry(4.6, 1.5, 1.35), body, -0.2, 0, 0);
    add(new THREE.CapsuleGeometry(0.62, 1.5, 5, 10), body, 1.9, -0.05, 0, 0, 0, Math.PI / 2, 1, 1, 1.05);
    // Tandem canopy — two stepped panes, gunner low and forward.
    add(new THREE.BoxGeometry(1.25, 0.62, 1.0), glass, 1.55, 0.5, 0, 0, 0, -0.13);
    add(new THREE.BoxGeometry(1.3, 0.72, 1.06), glass, 0.35, 0.72, 0, 0, 0, -0.05);
    // Engine housings either side of the mast.
    add(new THREE.CapsuleGeometry(0.4, 1.5, 4, 8), dark, -0.7, 0.62, 0.62, 0, 0, Math.PI / 2);
    add(new THREE.CapsuleGeometry(0.4, 1.5, 4, 8), dark, -0.7, 0.62, -0.62, 0, 0, Math.PI / 2);

    // Stub wings with pylons — the widest part of the silhouette from below.
    add(new THREE.BoxGeometry(1.0, 0.18, 5.2), body, -0.3, -0.15, 0);
    for (const sgn of [1, -1]) {
      add(new THREE.BoxGeometry(1.5, 0.42, 0.5), dark, -0.3, -0.42, sgn * 1.85);
      add(new THREE.CylinderGeometry(0.26, 0.26, 1.3, 8), dark, -0.3, -0.42, sgn * 2.45, 0, 0, Math.PI / 2);
    }

    // Chin turret and barrel.
    add(new THREE.SphereGeometry(0.34, 10, 8), dark, 1.75, -0.6, 0);
    add(new THREE.CylinderGeometry(0.09, 0.09, 1.0, 6), dark, 2.25, -0.72, 0, 0, 0, Math.PI / 2);

    // Tail boom, fin and stabiliser.
    add(new THREE.CylinderGeometry(0.34, 0.17, 4.2, 10), body, -4.2, 0.16, 0, 0, 0, Math.PI / 2);
    add(new THREE.BoxGeometry(0.7, 1.5, 0.14), body, -6.0, 0.8, 0, 0, 0, -0.18);
    add(new THREE.BoxGeometry(0.55, 0.12, 2.0), body, -5.5, 0.5, 0);
    // Wheels.
    add(new THREE.CylinderGeometry(0.22, 0.22, 0.2, 8), dark, -0.4, -0.95, 0.8, Math.PI / 2, 0, 0);
    add(new THREE.CylinderGeometry(0.22, 0.22, 0.2, 8), dark, -0.4, -0.95, -0.8, Math.PI / 2, 0, 0);

    // Main rotor: hub plus four blades on their own group so they spin.
    const rotor = new THREE.Group();
    rotor.position.set(-0.3, 1.15, 0);
    add(new THREE.CylinderGeometry(0.2, 0.26, 0.5, 8), dark, -0.3, 0.95, 0);
    for (let i = 0; i < 4; i++) {
      const blade = new THREE.Mesh(new THREE.BoxGeometry(5.6, 0.07, 0.42), dark);
      blade.position.set(2.6, 0, 0);
      const arm = new THREE.Group();
      arm.rotation.y = (i / 4) * Math.PI * 2;
      arm.add(blade);
      rotor.add(arm);
    }
    g.add(rotor);

    // Tail rotor, same treatment at a quarter of the size.
    const tail = new THREE.Group();
    tail.position.set(-6.0, 0.8, 0.22);
    for (let i = 0; i < 3; i++) {
      const blade = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.06, 0.22), dark);
      blade.position.set(0.7, 0, 0);
      const arm = new THREE.Group();
      arm.rotation.z = (i / 3) * Math.PI * 2;
      arm.add(blade);
      tail.add(arm);
    }
    g.add(tail);

    // A faint disc over the blades: on a still frame the blades read as crossed
    // planks, and this is what sells them as turning.
    const blur = new THREE.Mesh(
      new THREE.CircleGeometry(5.8, 32),
      new THREE.MeshBasicMaterial({
        color: 0x14181a, transparent: true, opacity: 0.16,
        side: THREE.DoubleSide, depthWrite: false,
      }),
    );
    blur.rotation.x = -Math.PI / 2;
    blur.position.set(-0.3, 1.15, 0);
    blur.renderOrder = 3;
    g.add(blur);

    g.userData.rotor = rotor;
    g.userData.tail = tail;
    g.scale.setScalar(1.25);
    return g;
  }

  /* ----------------------------------------------------------- update --- */

  update(dt) {
    const g = this.game;
    if (this.uav > 0) this.uav = Math.max(0, this.uav - dt);

    // Airstrike run.
    for (let i = this.strikes.length - 1; i >= 0; i--) {
      const b = this.strikes[i];
      b.t -= dt;
      if (b.t > 0) continue;
      this.strikes.splice(i, 1);
      g.detonate(b.at, STRIKE_RADIUS, STRIKE_DAMAGE, g.player);
    }

    if (this.heli) this._updateHeli(dt);
  }

  _updateHeli(dt) {
    const g = this.game;
    const h = this.heli;
    h.t -= dt;
    if (h.t <= 0) {
      g.engine.scene.remove(h.mesh);
      h.mesh.traverse((o) => { if (o.geometry) o.geometry.dispose(); });
      this.heli = null;
      return;
    }

    h.angle += dt * STREAK_RULES.heliOrbit;
    h.pos.set(
      h.centre.x + Math.cos(h.angle) * HELI_RADIUS,
      HELI_ALT,
      h.centre.z + Math.sin(h.angle) * HELI_RADIUS,
    );
    h.mesh.position.copy(h.pos);
    // Nose along the tangent of the orbit.
    h.mesh.rotation.y = -h.angle + Math.PI / 2;
    h.mesh.rotation.z = 0.16;                      // banked into the turn
    // Fast enough to blur into a disc on screen, which is the read we want.
    if (h.mesh.userData.rotor) h.mesh.userData.rotor.rotation.y += dt * 34;
    if (h.mesh.userData.tail) h.mesh.userData.tail.rotation.x += dt * 46;

    // Pick a target on a slow timer; the gunship is a threat, not a sniper.
    h.retarget -= dt;
    if (h.retarget <= 0 || !h.target || !h.target.alive) {
      h.retarget = 1.2;
      let best = null;
      let bestD = Infinity;
      // Bots offline, remote players online. The gunship never found a target
      // in a network game because `enemies` is empty there.
      for (const e of g.actors) {
        if (!e.alive || !this._isFoe(e)) continue;
        const d = e.root.position.distanceToSquared(h.centre);
        if (d < bestD && !g.physics.raycast(h.pos, _v.copy(e.root.position).sub(h.pos).normalize(),
          h.pos.distanceTo(e.root.position) - 1.2)) {
          bestD = d;
          best = e;
        }
      }
      h.target = best;
    }

    if (!h.target || !h.target.alive) return;

    // Sustained cannon fire rather than discrete shots: it is a chain gun.
    h.fire -= dt;
    if (h.fire <= 0) {
      h.fire = 0.09;
      const to = _v.copy(h.target.root.position);
      to.y += 1.0;
      to.x += (Math.random() - 0.5) * 1.8;
      to.z += (Math.random() - 0.5) * 1.8;
      _d.copy(to).sub(h.pos).normalize();
      g.effects?.tracer?.(h.pos.clone(), to.clone());
      g.effects?.impact?.(to.clone(), _d.clone().negate(), 'dirt');
      // Offline only: a remote player's health belongs to the server, and
      // damaging their rig here would be a local hallucination the next
      // snapshot erases. The gunship tracks and shoots online — it just cannot
      // hurt anyone until the server adjudicates it.
      // Same effective rate as before — the tenfold multiplier that used to be
      // here is now inside HELI_DPS, where it can be read.
      if (!g.net) h.target.damage(HELI_DPS * 0.09, 'body', _d.clone(), g.player);
      g.audio?.heliGun?.(h.pos);
    }
  }

  _isFoe(e) {
    const mine = this.game.player.team;
    return !mine || e.team !== mine;
  }

  /** HUD state: what is ready, what is running. */
  decorate(s) {
    s.streakReady = this.ready?.name ?? null;
    s.streakEarned = this.earned.map((t) => t.id);
    s.uavActive = this.uavActive;
    s.uavLeft = this.uav;
    s.heliActive = !!this.heli;
  }
}
